/**
 * IMPORTS
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";

// components
import { Button } from "@components/button/button";

import { useUpdateTarefa } from "@/hooks/minhas-tarefas/use-update-tarefa";
import { useStatusRemessa } from "@/hooks/remessas/status-remessa-do-cliente";

// utils / notify-messages
import { notifyError } from "@/utils/notify-messages";

// interfaces
import { IReturnShipmentModalProps } from "./interface";

const DevolverEnvioDadosModal = ({
  id_remessa,
  numero_remessa,
  onClose,
}: IReturnShipmentModalProps) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { mutate: mutateUseUpdateTarefa, isPending: isPendingUpdate } = useUpdateTarefa();
  const { statusRemessaAsync, isLoadingSolicitarRemessa } = useStatusRemessa();

  const is_pending = isPendingUpdate || isLoadingSolicitarRemessa;

  const handleConfirmar = useCallback(() => {
    mutateUseUpdateTarefa(
      {
        id_remessa: id_remessa,
        payload: {
          situacao: "em_producao",
          status: "em_producao",
          data_fim_producao: null,
        },
        ui: {
          successMessage: "Remessa retornada para produção!",
          errorMessage: "Erro ao retornar remessa para produção!",
        },
      },
      {
        onSuccess: async () => {
          try {
            await statusRemessaAsync({
              id_remessa: id_remessa,
              payload: { status: "em_producao" },
            });
            await queryClient.invalidateQueries({
              queryKey: ["search-shipment"],
              exact: false,
            });
            onClose();

            navigate("/?tab=my-tasks");
          } catch {
            notifyError("Remessa atualizada, mas falhou ao registrar histórico de status!");
            onClose();
          }
        },
        onError: () => {
          onClose();
        },
      }
    );
  }, [id_remessa, mutateUseUpdateTarefa, onClose, queryClient, statusRemessaAsync]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="max-h-[90vh] w-[400px] space-y-5 rounded-xl bg-white px-6 py-6 shadow-shape">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-800">Retornar remessa para solicitação</h2>
          <button type="button" onClick={onClose}>
            <X className="size-5 text-zinc-400" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-2 text-left">
          <p className="text-base font-medium text-zinc-700">
            Esta ação retornará a remessa <strong>{numero_remessa}</strong> para a etapa de envio de
            dados e alterará sua situação para <strong>"Solicitado"</strong>. Deseja continuar?
          </p>
        </div>

        <div className="flex w-full gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            className="w-full text-zinc-700"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button type="button" className="w-full" onClick={handleConfirmar} disabled={is_pending}>
            {is_pending ? "Retornando..." : "Confirmar"}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * EXPORTS
 */
export { DevolverEnvioDadosModal };
